import useSWR from 'swr'
import API from '../../../utils/api'
import { ADMIN_USERS } from '../../constants/APIKEYS'
import { useToken } from '../auth/token'
import { message } from 'antd'
import { useNotification } from '../useNotification'
import { useEffect, useState, useMemo, useCallback } from 'react'

export function useAllMembers(instanceKey?: string, initialState?: {
	level?: string
	order?: string
	ordertype?: string
	haswallet?: boolean
}) {
	const { data: token } = useToken()
	const { refresh } = useNotification('/admin/overview', true)
	
	// instanceKey를 기반으로 고유한 초기값 생성
	const getInitialValue = useCallback((key: string, defaultValue: any) => {
		if (!instanceKey) return defaultValue
		// instanceKey가 있으면 initialState 값 사용
		switch (key) {
			case 'level':
				return initialState?.level ?? defaultValue
			case 'order':
				return initialState?.order ?? defaultValue
			case 'ordertype':
				return initialState?.ordertype ?? defaultValue
			case 'haswallet':
				return initialState?.haswallet ?? defaultValue
			default:
				return defaultValue
		}
	}, [instanceKey, initialState])
	
	// 로컬 상태로 변경 - 초기값 지원
	const [page, setPage] = useState(1)
	const [haswallet, setHaswalletState] = useState(() => getInitialValue('haswallet', true))
	const [searchType, setSearchType] = useState('')
	const [searchWord, setSearchWord] = useState('')
	const [exceptType, setExceptTypeState] = useState('')
	const [level, setLevel] = useState(() => getInitialValue('level', 'ALL'))
	const [order, setOrder] = useState(() => getInitialValue('order', 'ASC'))
	const [ordertype, setOrdertype] = useState(() => getInitialValue('ordertype', 'id'))
	const [maxData, setMaxData] = useState(20)
	const [group, setGroup] = useState('*')
	
	// initialState가 변경되면 상태 업데이트
	useEffect(() => {
		if (initialState?.level !== undefined) {
			setLevel(initialState.level)
			setPage(1) // 탭 변경 시 페이지 리셋
			setSearchType('') // 검색 초기화
			setSearchWord('')
		}
		if (initialState?.order !== undefined) {
			setOrder(initialState.order)
		}
		if (initialState?.ordertype !== undefined) {
			setOrdertype(initialState.ordertype)
		}
		if (initialState?.haswallet !== undefined) {
			setHaswalletState(initialState.haswallet)
		}
	}, [initialState?.level, initialState?.order, initialState?.ordertype, initialState?.haswallet])
	
	// URL 생성을 useMemo로 최적화 - instanceKey를 포함하여 각 인스턴스별로 독립적인 키 생성
	const requestKey = useMemo(() => {
		if (!token) return null
		
		const baseKey = `${ADMIN_USERS}?group=${group}&maxData=${maxData}${
			haswallet ? '&haswallet=1' : ''
		}&page=${page}${
			level !== 'ALL' ? `&level=${level}` : ''
		}${
			searchType !== '' && searchWord !== ''
				? `&type=${searchType}&searchWord=${searchWord}`
				: ''
		}&order=${order}&ordertype=${ordertype}&exceptType=${exceptType}`
		
		// instanceKey가 있으면 추가하여 각 컴포넌트별로 독립적인 SWR 키 생성
		return instanceKey ? `${baseKey}&instance=${instanceKey}` : baseKey
	}, [page, haswallet, searchType, searchWord, exceptType, level, order, ordertype, maxData, group, token, instanceKey])

	const { data, mutate, isLoading, isValidating } = useSWR<any>(
		requestKey,
		requestKey ? async (key) => {
			// instance 파라미터 제거하고 실제 API 호출
			const actualUrl = key.split('&instance=')[0]
			const response = await API.get(actualUrl, token + '', {})
			return response.result.result
		} : null,
		{
			revalidateOnFocus: false,
			revalidateIfStale: false,
			revalidateOnMount: true, // 초기 데이터 가져오기
			revalidateOnReconnect: false,
			dedupingInterval: 10000, // 10초간 중복 요청 방지
			shouldRetryOnError: false,
			errorRetryCount: 0, // 에러 시 재시도 안함
			refreshInterval: 0, // 자동 새로고침 비활성화
			keepPreviousData: true, // 이전 데이터 유지
		}
	)
	
	// 페이지 변경
	const changePage = useCallback(async (value: number) => {
		setPage(value)
		return mutate()
	}, [mutate])
	
	// 레벨 변경
	const changeLevel = useCallback(async (value: string) => {
		setLevel(value)
		return mutate()
	}, [mutate])
	
	// 그룹 변경
	const changeGroup = useCallback(async (value: string) => {
		setGroup(value)
		return mutate()
	}, [mutate])
	
	// 지갑 보유 여부 변경
	const setHaswallet = useCallback(async (value: boolean) => {
		setHaswalletState(value)
		return mutate()
	}, [mutate])
	
	// 최대 데이터 수 변경
	const changeMaxData = useCallback(async (value: number) => {
		setMaxData(value)
		setPage(1)
		return mutate()
	}, [mutate])
	
	// 검색
	const searchWords = useCallback(async (type: string, word: string) => {
		setPage(1)
		setSearchType(type)
		setSearchWord(word)
		return mutate()
	}, [mutate])
	
	// 사용자 수정
	const editUser = useCallback(async (value: any) => {
		try {
			const res = await API.put(`/admin/user`, token + '', value)
			if (res.result.success) {
				message.success('수정되었습니다.')
				setTimeout(() => {
					refresh()
					mutate()
				}, 100)
			} else {
				message.error('수정에 실패하였습니다.\n' + res?.result?.message)
			}
		} catch (err) {
			message.error('수정에 실패하였습니다.')
		}
	}, [token, refresh, mutate])
	
	// 정렬 타입 변경 - mutate 호출 제거
	const setOrderType = useCallback((value: string) => {
		setOrdertype(value)
	}, [])
	
	// 제외 타입 설정 - mutate 호출 제거
	const setExceptType = useCallback((value: string) => {
		setExceptTypeState(value)
	}, [])
	
	// 정렬 순서 변경 - mutate 호출 제거
	const orderSort = useCallback((value: string) => {
		if (value === 'ASC' || value === 'DESC') {
			setOrder(value)
		}
	}, [])
	
	return {
		data,
		isLoading,
		searchType,
		searchWord,
		maxData,
		haswallet,
		exceptType,
		level,  // 실제 level 값 반환
		mutate: changePage,
		changeLevel,  // level 변경 함수는 changeLevel로 반환
		setGroup: changeGroup,
		setHaswallet,
		setMaxData: changeMaxData,
		searchWords,
		editUser,
		setOrderType,
		setExceptType,
		orderSort,
	}
}